export const enHero = {
  badge: 'Enterprise-grade ordering platform',
  title_part1: 'Take control of your',
  title_part2: ' online orders',
  subtitle:
    'Lewouhmart unifies product catalog, inventory, payments, and deliveries into one secure, global-ready platform for your team and your customers.',
  cta_primary: 'Get started',
  cta_secondary: 'Live demo',
  feature_iam_title: 'IAM & Security',
  feature_iam_body:
    'Role-based access, MFA for employees, and transactional safety by design.',
  feature_global_title: 'Global-ready',
  feature_global_body:
    'Multi-language and multi-currency support from day one.',
  feature_stock_title: 'Reliable stock',
  feature_stock_body:
    'Concurrency-safe stock control with accurate profit tracking.',
  carousel_section_label: 'Platform highlights',
  carousel_iam_title: 'Secure IAM for your whole team',
  carousel_iam_subtitle:
    'Employees sign in with MFA and role-based access, while customers enjoy a simple email + password flow.',
  carousel_global_title: 'Global-ready from day one',
  carousel_global_subtitle:
    'Serve customers in multiple languages and currencies, with preferences that travel across devices.',
  carousel_stock_title: 'Reliable stock and fulfillment',
  carousel_stock_subtitle:
    'Concurrency-safe stock control and delivery tracking keep your operations accurate and predictable.',
} as const;

export const enAuth = {
  tab_sign_in: 'Sign in',
  tab_register: 'Create account',
  sign_in_title: 'Sign in to Lewouhmart',
  sign_in_subtitle:
    'Use your email and password to access your orders and account settings.',
  sign_in_customer_hint:
    'Sign in with the email and password for your customer account.',
  register_title: 'Create your customer account',
  register_subtitle:
    "Use your email and a strong password. We'll collect your delivery address in the format carriers like FedEx need for shipping.",
  email_label: 'Email address',
  password_label: 'Password',
  password_placeholder: 'At least 8 characters',
  sign_in_button: 'Continue',
  register_button: 'Create account',
  register_invite_applied:
    "You're signing up with referral code {{code}}. Eligibility is checked when you complete a qualifying first order.",
  register_invite_code_label: 'Referral code (optional)',
  register_invite_code_placeholder: "Friend's invite code",
  register_invite_code_hint:
    'Leave blank if you do not have one. A code from an invite link (?ref=…) is applied automatically.',
  register_apply_referral_checkbox: 'Apply a referral code',
  register_referral_required_when_checked:
    'Enter a referral code or uncheck the box to continue without one.',
  mfa_title: 'Enter your security code',
  mfa_body_intro: 'We sent a 6-digit code to',
  mfa_body_outro: 'Enter it to finish signing in.',
  mfa_button: 'Verify and continue',
  mfa_resend: 'Resend code',
  mfa_resend_sending: 'Sending…',
  mfa_resend_wait: 'Resend code in {{seconds}}s',
  mfa_resend_failed: 'Unable to resend code.',
  mfa_back_to_sign_in: 'Back to sign in',
  mfa_resend_policy:
    'You can resend up to 3 times per hour. If email still does not arrive, check spam or your server email (SES) settings.',
  signed_in_title: "You're signed in",
  signed_in_body:
    'Your language and currency preferences are synchronized with your profile. Future sessions and other clients (like a mobile app) will be able to reuse the same settings.',
  signed_in_label: 'Signed in as',
  signed_in_logout: 'Sign out',
  signed_in_email_fallback: 'Authenticated user',
  register_success_prefix: 'Account created for',
  register_success_suffix:
    'We sent a verification email. Please confirm it in your inbox before signing in.',
  register_success_banner_title: 'Check your inbox — one more step',
  register_form_locked_note:
    'This form is locked until you verify your email. You can close this tab after opening the message we sent.',
  verify_email_resent:
    'Your email is not yet verified. We have sent you another verification email. Please confirm it before trying again.',
  verify_email_heading: 'Verify your email address',
  verify_email_loading: 'Verifying your email, please wait…',
  verify_email_success:
    'Your email has been successfully verified. You can now sign in.',
  verify_email_error:
    'We could not verify your email. The link may have expired or already been used.',
  verify_email_missing_token:
    'Verification link is invalid. Please use the link from your email or request a new one.',
  verify_email_back_to_signin: 'Back to sign in',
  forgot_link: 'Forgot your password?',
  forgot_title: 'Reset your password',
  forgot_subtitle:
    'Enter your email address and we will send you a link to reset your password.',
  forgot_submit: 'Send reset link',
  forgot_sending: 'Sending…',
  forgot_success: 'If an account exists, a reset link has been sent.',
  forgot_error: 'Unable to send reset email.',
  reset_title: 'Choose a new password',
  reset_subtitle: 'Set a new password for your Lewouhmart account.',
  reset_password_label: 'New password',
  reset_confirm_password_label: 'Confirm new password',
  reset_submit: 'Save new password',
  reset_saving: 'Saving…',
  reset_mismatch: 'Passwords do not match.',
  reset_invalid_link: 'This reset link is invalid. Please use the link from your email.',
  reset_success: 'Your password has been updated. You can now sign in.',
  reset_error: 'Unable to reset password.',
  reset_back_to_signin: 'Back to sign in',
  email_placeholder: 'you@company.com',
  account_deleted_notice:
    "Your account was deleted. We're sorry to see you go.",
  security_block_title: 'Account security',
  security_block_loading: 'Confirming…',
  security_block_success:
    'Your staff account has been blocked. All active sessions were ended. Contact an administrator if you need access restored.',
  security_block_error_generic: 'This link is invalid, already used, or expired.',
  security_block_missing_token: 'No security token was provided.',
  security_block_back_sign_in: 'Back to sign in',
  full_name_label: 'Full name',
  phone_label: 'Phone',
  register_step_of: 'Step {{step}} of 2',
  register_continue_delivery: 'Continue to delivery address',
  register_complete_account_details: 'Please complete your account details first.',
  register_password_mismatch: 'Password confirmation does not match.',
  register_complete_delivery_address: 'Please complete your delivery address.',
  register_unable_create: 'Unable to create account.',
  register_creating_state: 'Creating account…',
  confirm_password_label: 'Confirm password',
  confirm_password_placeholder: 'Repeat your password',
  verification_email_sent: 'Verification email sent',
  delivery_address_title: 'Delivery address',
  delivery_address_subtitle:
    'Enter your address exactly as it should appear on shipping labels. FedEx and other carriers use these fields to validate delivery.',
  country_region_label: 'Country / region',
  country_search_select: 'Search and select your country',
  street_line1_label: 'Street address',
  street_line1_placeholder: 'House number and street name',
  street_line2_label: 'Apartment, suite, unit (optional)',
  street_line2_placeholder: 'Apt 4B, Suite 200, Floor 3…',
  city_label: 'City',
  state_province_label: 'State / province',
  state_province_placeholder: 'e.g. CA, NY, ON',
  postal_code_label: 'Postal / ZIP code',
  address_search_label: 'Find my address (optional)',
  address_search_placeholder: 'Start typing your address…',
  address_search_hint: 'Select a suggestion to fill the fields below, or enter your address manually.',
  address_preview_label: 'Address preview',
  address_selected: 'Verified suggestion applied:',
  address_manual: 'Manual entry:',
  address_verify_background:
    'We will verify this address in the background after account creation.',
  state_required_hint: 'Required for shipping to your country.',
  detecting_location: 'Detecting…',
  use_my_location: 'Use my location',
  geo_not_supported: 'Geolocation is not supported in this browser.',
  detect_country_failed: 'Unable to detect country.',
  detect_country_unknown: 'Could not determine your country from your location.',
  geo_permission_failed: 'Could not get location permission.',
} as const;

export const enLayout = {
  app_name: 'Lewouhmart',
  app_tagline: 'Commerce & order management',
  footer_right:
    'Designed for reliability, concurrency, and global growth.',
} as const;

export const enAccount = {
  title: 'My account',
  subtitle: 'Manage your profile, sign-in security, and default delivery address.',
  sign_in_required: 'Please sign in to view your account.',
  profile_section_title: 'Contact & delivery',
  profile_section_subtitle: 'Used on orders, invoices, and shipping labels.',
  field_full_name: 'Full name',
  field_phone: 'Phone',
  field_street: 'Street address',
  field_city: 'City',
  field_postal_code: 'Postal code',
  field_country: 'Country',
  readonly_name_label: 'Name',
  readonly_phone_label: 'Phone',
  readonly_address_label: 'Address',
  not_set: 'Not set',
  save_button: 'Save changes',
  cancel_button: 'Cancel',
  edit_button: 'Edit',
  status_label: 'Status',
  status_verified: 'Verified',
  status_pending: 'Pending verification',
  email_label: 'Account email',
  load_error: 'Unable to load account information.',
  save_success: 'Profile updated successfully.',
  save_error: 'Unable to save profile.',
  security_section_title: 'Security',
  current_password_label: 'Current password',
  new_password_label: 'New password',
  confirm_password_label: 'Confirm new password',
  password_mismatch: 'Passwords do not match.',
  password_save_button: 'Update password',
  password_saving_label: 'Updating…',
  password_change_success: 'Password updated successfully.',
  password_change_error: 'Unable to change password.',
  password_open_button: 'Change password',
  delete_section_title: 'Close your account',
  delete_section_intro:
    'Deleting your account removes access to this profile. Order records may be kept for legal and operational needs, but they will no longer be tied to you.',
  delete_email_retention:
    'To help prevent fraud and referral abuse, we retain a normalized form of your email address indefinitely. If you sign up again with the same email, you receive a new profile with no link to previous orders or referral activity.',
  delete_phrase_hint_before: 'Type exactly:',
  delete_password_label: 'Current password (required)',
  delete_confirm_label: 'Type this phrase to confirm',
  delete_confirm_placeholder: 'I-want-to-delete-account',
  delete_confirm_mismatch:
    'Type the exact phrase I-want-to-delete-account in the confirmation field.',
  delete_password_required: 'Enter your current password.',
  delete_submit: 'Delete my account',
  delete_submitting: 'Deleting…',
  delete_error: 'Could not delete account. Check your password.',
  delete_success_redirect: 'Your account has been deleted.',
  retention_offer_percent:
    'If you stay: {{percent}}% off your next order’s merchandise subtotal (one time, applied at checkout).',
  retention_accepted_with_percent:
    'Thanks for staying. Your next order will include a one-time {{percent}}% discount on merchandise (subtotal), shown at checkout and on your receipt.',
  retention_banner_title: 'Stay discount unlocked',
  retention_banner_dismiss: 'Dismiss',
  retention_cancel: 'Cancel',
  retention_email_cooldown_notice:
    'This email address recently used the stay offer or one-time deletion discount. The offer is unavailable until {{date}} (UTC).',
  sidebar_overview: 'Account',
  sidebar_orders: 'Orders',
  sidebar_referrals: 'Referrals',
  sidebar_security: 'Security',
  sidebar_notifications: 'Notifications',
  orders_title: 'My orders',
  orders_subtitle: 'Track your order status and history.',
  orders_search_placeholder: 'Search order ID or item',
  orders_empty: 'No orders yet.',
  orders_empty_filtered: 'No orders match your filters.',
  order_id: 'Order',
  payment_id: 'Payment ID',
  payment_id_none: '—',
  preorder_payments_heading: 'Pre-order card payments',
  preorder_deposit_payment_label: 'Deposit charge ID',
  preorder_balance_payment_label: 'Balance charge ID',
  preorder_balance_payments_label: 'Balance charge IDs',
  preorder_balance_payment_index: '{{n}}.',
  preorder_balance_payment_pending: 'Not charged yet (due after release)',
  items: 'Items',
  eta: 'Estimated delivery',
  eta_days: '{{days}} days',
  delivery_window_from: 'From',
  delivery_window_until: 'Until',
  order_history: 'Order history',
  cancel_order: 'Cancel order',
  order_collapsed_hint: 'Closed order collapsed. Expand to view details.',
  orders_pagination_info: 'Page {{page}} of {{totalPages}}',
  orders_load_error: 'Unable to load orders.',
  orders_cancel_error: 'Unable to cancel order.',
  carrier_tracking_title: 'Shipment tracking',
  carrier_tracking_subtitle: 'Live scan updates from your carrier.',
  carrier_tracking_number: 'Tracking #',
  carrier_tracking_open_carrier: 'Open on carrier site',
  carrier_tracking_last_sync: 'Last updated {{time}}',
  carrier_tracking_refresh: 'Refresh',
  carrier_tracking_refreshing: 'Updating…',
  carrier_tracking_refresh_error: 'Could not refresh tracking.',
  carrier_tracking_no_events:
    'No scan events yet. Tracking updates appear here after the carrier scans your package.',
  carrier_tracking_event_unknown: 'Scan update',
  carrier_status_LABEL_CREATED: 'Label created',
  carrier_status_PICKUP_SCHEDULED: 'Pickup scheduled',
  carrier_status_IN_TRANSIT: 'In transit',
  carrier_status_DELIVERED: 'Delivered',
  carrier_status_EXCEPTION: 'Exception',
  carrier_status_CANCELLED: 'Cancelled',
  order_receipt_download: 'Receipt (PDF)',
  order_receipt_error: 'Could not download receipt.',
  order_event_update: 'Update',
  order_event_change: '{{from}} → {{to}}',
  saving_label: 'Saving…',
  order_status_PENDING: 'Pending',
  order_status_PAID: 'Paid',
  order_status_SHIPPED: 'Shipped',
  order_status_DELIVERED: 'Delivered',
  order_status_CLOSED: 'Closed',
  order_status_CANCELLED: 'Cancelled',
  order_status_UNREACHABLE: 'Unreachable',
  order_status_DAMAGED: 'Damaged',
  order_status_INVESTIGATING: 'Investigating',
  order_status_FAULT_EMPLOYEE: 'Fault (employee)',
  order_status_FAULT_CUSTOMER: 'Fault (customer)',
  order_status_RESHIP_PENDING: 'Reship pending',
  order_status_RESHIPPED: 'Reshipped',
  referrals_copied: 'Copied!',
  referrals_status_pending: 'Waiting for qualifying order',
  referrals_status_qualified: 'Completed',
  referrals_status_rejected: 'Not eligible',
  referrals_signup_only_note:
    'Referral links and codes are used when someone creates an account—not at checkout. If you signed up with a friend’s link, it is already linked to your profile; you do not enter it again when paying.',
  referrals_pending_hint:
    'You do not need anyone to validate this. When your first order is paid and meets the minimum total ({{min}} USD), the referral completes automatically. You never enter a referral code during checkout.',
  referrals_qualified_hint:
    'Your referral is complete: you registered with a friend’s link and finished a qualifying first order. The amounts shown are the program’s reward targets—they are not applied as an extra field at checkout. How rewards are delivered (credit, payout, etc.) is up to this store; contact support if you expected an immediate discount on your order.',
  referrals_rejected_hint:
    'This referral could not be counted. Contact support if you have questions.',
  referrals_subtitle_ended:
    'This referral program is no longer open to new participants. Your past activity is shown below.',
  referrals_program_ended_banner:
    'The referral program is no longer open to new participants. Your history below is preserved. Pending referrals that started before the program ended can still complete when qualifying orders are paid.',
  referrals_program_ended_no_new_links:
    'New invite links are disabled. This was your code while the program was active.',
  referrals_disabled: 'The referral program is not active right now.',
} as const;

export const enHeader = {
  brand_name: 'Lewouhmart',
  brand_tagline: 'Sell smarter, fulfill faster.',
  nav_home: 'Home',
  nav_cart: 'Cart',
  nav_admin_dashboard: 'Dashboard',
  nav_admin_employees: 'Employees',
  nav_admin_customers: 'Customers',
  nav_products: 'Products',
  nav_products_catalog: 'Catalog',
  nav_products_inventory: 'Inventory & stock',
  logged_in_as: 'Logged in as',
  account_button: 'Account',
  logout_button: 'Logout',
} as const;

export const enFooter = {
  copyright: 'Lewouhmart. All rights reserved.',
  footer_right:
    'Designed for reliability, concurrency, and global growth.',
  workspace_internal: 'Staff workspace.',
  workspace_open_storefront: 'Customer site',
  customer_heading: 'Customer',
  link_home: 'Home',
  link_products: 'Products',
  link_cart: 'Cart',
  link_track_orders: 'Track orders',
  link_feedback_complaints: 'Feedback & complaints',
  link_about: 'About us',
  link_cookie_settings: 'Cookie settings',
  ops_heading: 'Operations',
  link_employee_signin: 'Employee sign in',
  ops_live_workflow: 'Live order workflow',
  ops_incidents: 'Incident & resolution handling',
  ops_analytics: 'Country-level order analytics',
  support_heading: 'Support & legal',
  terms: 'Terms of service',
  privacy: 'Privacy policy',
  storefront_tagline: 'Built for reliable ordering at scale.',
  storefront_about:
    'Global ordering, inventory and delivery tracking platform for modern commerce teams.',
} as const;

export const enAdmin = {
  sidebar_overview: 'Overview',
  sidebar_employees: 'Employees',
  sidebar_customers: 'Customers',
  sidebar_customer_archives: 'Deleted accounts archive',
  sidebar_products: 'Products',
  sidebar_purchases: 'Purchases',
  sidebar_categories: 'Categories',
  sidebar_inventory: 'Inventory',
  sidebar_group_reports: 'Stats & reports',
  sidebar_reports: 'Budget & activity reports',
  sidebar_hero: 'Landing hero',
  sidebar_landing_promotion: 'Landing promotion',
  sidebar_mail_routing: 'Email senders',
  sidebar_budget: 'Budget',
  sidebar_payroll: 'Payroll',
  sidebar_currencies: 'Currencies',
  sidebar_expiry_alerts: 'Cron jobs',
  sidebar_group_users: 'Users',
  sidebar_group_products: 'Products',
  sidebar_group_settings: 'Settings',
  sidebar_group_site_settings: 'Site settings',
  sidebar_group_system: 'System settings',
  sidebar_docs: 'Documentation',
  sidebar_technical_docs: 'Technical documentation',
  sidebar_suppliers: 'Suppliers',
  sidebar_roles: 'Roles',
  suppliers_title: 'Suppliers',
  suppliers_subtitle:
    'Manage your suppliers. Only employees can create and edit suppliers; suppliers do not log in.',
  suppliers_search: 'Search by name, email or phone',
  suppliers_active_only: 'Active only',
  suppliers_new: 'New supplier',
  suppliers_hide_form: 'Hide form',
  suppliers_name: 'Name',
  suppliers_email: 'Email',
  suppliers_phone: 'Phone',
  suppliers_country: 'Country',
  suppliers_country_placeholder: 'Select a country',
  suppliers_city: 'Town / City',
  suppliers_address: 'Address',
  suppliers_notes: 'Notes',
  suppliers_save: 'Save supplier',
  suppliers_update: 'Update supplier',
  suppliers_loading: 'Loading suppliers…',
  suppliers_empty: 'No suppliers found.',
  suppliers_col_name: 'Name',
  suppliers_col_email: 'Email',
  suppliers_col_phone: 'Phone',
  suppliers_col_country: 'Country',
  suppliers_col_city: 'Town / City',
  suppliers_col_status: 'Status',
  suppliers_status_active: 'Active',
  suppliers_status_inactive: 'Inactive',
  suppliers_edit: 'Edit',
  suppliers_activate: 'Activate',
  suppliers_deactivate: 'Deactivate',
  suppliers_load_error: 'Unable to load suppliers.',
  suppliers_save_error: 'Unable to save supplier.',
  suppliers_update_error: 'Unable to update supplier.',
  dashboard_title: 'Admin workspace',
  dashboard_subtitle: 'Monitor your team and customer base from a single place.',
  dashboard_card_employees_title: 'Employees',
  dashboard_card_employees_body:
    'Overview of all staff accounts with MFA enforced.',
  dashboard_card_customers_title: 'Customers',
  dashboard_card_customers_body:
    'Registered customers with verified email addresses.',
  dashboard_card_security_title: 'Security',
  dashboard_card_security_body:
    'Employees always authenticate with an additional email code.',

  employees_title: 'Employees',
  employees_subtitle: 'Invite staff to Lewouhmart and manage their basic details.',
  employees_invite_title: 'Invite new employee',
  employees_invite_email_label: 'Work email',
  employees_invite_name_label: 'Full name',
  employees_invite_role_label: 'Job title',
  employees_invite_phone_label: 'Phone',
  employees_invite_type_label: 'Account type',
  employees_invite_type_employee: 'Employee',
  employees_invite_type_admin: 'Admin',
  employees_invite_type_shipping_agent: 'Shipping agent',
  roles_select_placeholder: 'Select a role',
  employees_invite_submit: 'Send invite',
  employees_invite_sending: 'Sending…',
  employees_invite_success: 'Invitation email sent to employee.',
  employees_invite_error: 'Unable to send invite.',
  employees_table_title: 'Team members',
  employees_table_loading: 'Loading employees…',
  employees_table_empty: 'No employees yet. Send an invite to get started.',
  employees_table_col_name: 'Name',
  employees_table_col_email: 'Email',
  employees_table_col_role: 'Role',
  employees_table_col_status: 'Status',
  employees_blocked_intrusion: 'Blocked via self-service (intrusion)',
  employees_table_col_roles: 'Roles',
  employees_table_status_active: 'ACTIVE',
  employees_action_enable: 'Enable',
  employees_action_disable: 'Disable',
  employees_status_error: 'Unable to update employee status.',
  employees_invite_show_button: 'Add employee',
  employees_invite_hide_button: 'Hide form',
  employees_invite_description: 'Invite a new team member to Lewouhmart.',
  employees_roles_manage: 'Manage roles',
  employees_roles_modal_title: 'Assign roles',
  employees_roles_load_error: 'Unable to load roles.',
  employees_roles_save_error: 'Unable to update roles.',

  roles_title: 'Roles & permissions',
  roles_subtitle: 'Create roles and assign fine-grained permissions for each feature.',
  roles_list_title: 'Roles',
  roles_new: 'New role',
  roles_field_name: 'Role name',
  roles_name_placeholder: 'e.g. ACCOUNTANT',
  roles_field_description: 'Description',
  roles_description_placeholder: 'Short description',
  roles_create: 'Create role',
  roles_manage: 'Manage',
  roles_badge_system: 'System',
  roles_col_name: 'Name',
  roles_col_description: 'Description',
  roles_col_permissions: 'Permissions',
  roles_col_users: 'Users',
  roles_empty: 'No roles yet.',
  roles_load_error: 'Unable to load roles.',
  roles_create_error: 'Unable to create role.',
  roles_manage_title: 'Manage role',
  roles_group_help: 'Select what this role can do in this area.',
  roles_group_select_all: 'Select all',
  roles_group_clear: 'Clear',
  roles_group_all: 'All',
  roles_group_some: 'Some',
  roles_group_none: 'None',
  roles_perm_catalog_error: 'Unable to load permission catalog.',
  roles_perm_load_error: 'Unable to load role permissions.',
  roles_perm_save_error: 'Unable to save permissions.',
  roles_perm_save_success: 'Permissions saved.',

  customers_title: 'Customers',
  customers_subtitle:
    'Filter the directory, sort by spend or order volume, then open a customer for full profile and orders.',
  customers_loading: 'Loading customers…',
  customers_empty: 'No customers match your filters.',
  customers_error: 'Unable to load customers.',
  customers_table_col_name: 'Name',
  customers_table_col_email: 'Email',
  customers_table_col_phone: 'Phone',
  customers_table_col_status: 'Status',
  customers_filter_section: 'Filters',
  customers_filter_show: 'Show filters',
  customers_filter_hide: 'Hide filters',
  customers_filter_collapsed_hint:
    'Filters are hidden for a clearer table view. Active search criteria still apply.',
  customers_filter_section_hint:
    'Country and city offer suggestions from your customer profiles. Set country first to narrow cities.',
  customers_suggest_loading: 'Loading suggestions…',
  customers_suggest_error: 'Could not load suggestions. Check your connection or try again.',
  customers_suggest_no_countries: 'No countries saved on customer profiles yet.',
  customers_suggest_no_cities: 'No cities saved on customer profiles yet.',
  customers_suggest_no_match: 'No matching value in your customer data.',
  customers_filter_search: 'Search (name, email, phone)',
  customers_filter_search_ph: 'Type to filter…',
  customers_filter_phone: 'Phone',
  customers_filter_phone_ph: 'Digits or +prefix',
  customers_filter_country: 'Country',
  customers_filter_country_ph: 'e.g. United States',
  customers_filter_city: 'City',
  customers_filter_city_ph: 'e.g. New York',
  customers_filter_registered_from: 'Registered from',
  customers_filter_registered_to: 'Registered to',
  customers_filter_apply: 'Apply filters',
  customers_filter_reset: 'Reset',
  customers_sort_label: 'Sort by',
  customers_sort_created_desc: 'Newest registered',
  customers_sort_created_asc: 'Oldest registered',
  customers_sort_spend_desc: 'Highest total spend (USD)',
  customers_sort_spend_asc: 'Lowest total spend (USD)',
  customers_sort_orders_desc: 'Most orders',
  customers_sort_orders_asc: 'Fewest orders',
  customers_col_orders: 'Orders',
  customers_col_spend: 'Spend (USD)',
  customers_col_city: 'City',
  customers_col_country: 'Country',
  customers_col_drive_time: 'Drive time',
  customers_col_actions: 'Actions',
  customers_open_detail: 'View',
  customer_archives_title: 'Deleted accounts archive',
  customer_archives_subtitle:
    'Immutable snapshots from when customers deleted their accounts. Orders remain in the system (unlinked); open an archive for the full JSON snapshot and links to live orders.',
  customer_archives_search_label: 'Search',
  customer_archives_search_ph: 'Normalized email or former user id',
  customer_archives_search_submit: 'Search',
  customer_archives_empty: 'No deletion archives yet.',
  customer_archives_error: 'Unable to load deletion archives.',
  customer_archives_col_deleted: 'Deleted at',
  customer_archives_col_name: 'Name (snapshot)',
  customer_archives_col_email: 'Email (normalized)',
  customer_archives_col_former_id: 'Former user id',
  customer_archives_col_orders: 'Orders (snapshot)',
  customer_archives_col_actions: 'Actions',
  customer_archives_open: 'Open',
  customer_archives_page_range: '{{from}}–{{to}} of {{total}}',
  customer_archive_detail_back: 'Archive list',
  customer_archive_detail_title: 'Deletion archive',
  customer_archive_detail_subtitle:
    'Compliance snapshot at deletion time. Use live orders when you have ORDER:READ.',
  customer_archive_detail_summary: 'Summary',
  customer_archive_detail_trigger: 'Trigger',
  customer_archive_detail_orders_live: 'Orders (live)',
  customer_archive_detail_order_id: 'Order id',
  customer_archive_detail_batch: 'Delivery batch',
  customer_archive_detail_open_order: 'Open in orders',
  customer_archive_detail_snapshot_json: 'Full snapshot (JSON)',
  customer_archive_detail_snapshot_hint:
    'Includes profile, order ids at deletion, referral data, exit survey, and preview counts.',
  customer_archive_detail_not_found: 'Archive not found.',
  customer_archive_detail_error: 'Unable to load archive.',
  customers_total_count: '{{count}} customers',
  customers_page_range: '{{from}}–{{to}} of {{total}}',
  customers_page_of: 'Page {{page}} / {{pages}}',
  customers_page_prev: 'Previous',
  customers_page_next: 'Next',
  customers_rows_per_page: 'Rows per page',
  customers_page_showing: 'Page {{page}} / {{pages}}',
  customers_detail_back: 'Back to customers',
  customers_detail_loading: 'Loading customer…',
  customers_detail_error: 'Unable to load customer.',
  customers_detail_not_found: 'Customer not found.',
  customers_detail_total_spend: 'Total spend',
  customers_detail_orders: 'orders',
  customers_detail_profile: 'Profile',
  customers_detail_id: 'Customer ID',
  customers_detail_address: 'Address',
  customers_detail_lat_lng: 'Lat / Lng',
  customers_detail_drive_time: 'Drive time (store → address)',
  customers_detail_drive_status: 'Route status',
  customers_detail_created: 'Created',
  customers_detail_last_login: 'Last login',
  customers_detail_preferences: 'Preferences',
  customers_detail_order_summary: 'Order summary',
  customers_detail_last_order: 'Last order',
  customers_detail_status_counts: 'Status counts',
  customers_detail_orders_section: 'Orders',
  customers_detail_no_orders: 'No orders for this customer yet.',
  sidebar_account_deletion_retention: 'Account Deletion',
  customers_detail_account_lifecycle: 'Account deletion & retention',
  customers_detail_account_lifecycle_hint:
    'Retention record for this email, archived deletions (including exit survey), and recent delete-flow events for this account.',
  customers_detail_retained_email_title: 'Retained email record',
  customers_detail_delete_count: 'Prior account deletions (this email)',
  customers_detail_first_seen: 'First retained',
  customers_detail_last_deleted: 'Last deleted at',
  customers_detail_archives: 'Deletion archives',
  customers_detail_archive_deleted_at: 'Deleted',
  customers_detail_exit_survey: 'Exit survey',
  customers_detail_exit_reasons: 'Selected reasons',
  customers_detail_exit_comment: 'Comment',
  customers_detail_recent_attempts: 'Recent deletion attempts',
  customers_detail_attempt_kind: 'Kind',
  customers_detail_attempt_at: 'When',
  account_deletion_retention_title: 'Account deletion & retention',
  account_deletion_retention_blurb:
    'Configure the stay offer (title, body, optional promo code, and one-time % discount on the next order), preview limits, and exit survey options when the offer cap is reached.',
  account_deletion_retention_load_error: 'Unable to load account deletion retention settings.',
  account_deletion_retention_saved: 'Account deletion retention settings saved.',
  account_deletion_retention_save_error: 'Unable to save settings.',
  account_deletion_retention_enabled: 'Enable retention flow',
  account_deletion_retention_window_days: 'Rolling window (days)',
  account_deletion_retention_window_days_hint: 'Previews in this window count toward the offer limit.',
  account_deletion_retention_max_offers: 'Max retention offers per window',
  account_deletion_retention_max_offers_hint:
    'After this many previews in the window, the retention offer is hidden and the exit survey is required.',
  account_deletion_retention_offer_title: 'Offer title',
  account_deletion_retention_offer_body: 'Offer body',
  account_deletion_retention_promo_code: 'Optional promo code',
  account_deletion_retention_promo_hint:
    'Shown to the customer; must exist in Promotions if you want it to apply automatically.',
  account_deletion_retention_exit_reasons: 'Exit survey reasons',
  account_deletion_retention_add_reason: 'Add reason',
  account_deletion_retention_exit_reasons_hint:
    'When the offer cap is exceeded, the customer must pick from these before deleting.',
  account_deletion_retention_label: 'Label',
  account_deletion_retention_discount_percent: 'One-time stay discount (% of subtotal)',
  account_deletion_retention_discount_percent_hint:
    'Applied once on the customer’s next order after they accept the stay offer (stored on the order for finance and staff).',
  account_deletion_retention_email_cooldown_days: 'Per-email offer cooldown (days)',
  account_deletion_retention_email_cooldown_days_hint:
    'After this email accepts the stay offer or uses the one-time discount, the retention offer is blocked for this many days (even if they delete the account and register again). Use 0 to disable.',
  orders_discount_retention: 'Account retention (stay offer)',
  customers_detail_col_id: 'ID',
  customers_detail_col_date: 'Date',
  customers_detail_col_total: 'Total',
  customers_detail_col_city: 'City',
  customers_detail_items_count: '{{count}} items',
  // Categories
  categories_title: 'Categories',
  categories_subtitle: 'Manage product categories for the store and landing page.',
  categories_count: 'categories',
  categories_add: 'Add category',
  categories_name: 'Name',
  categories_slug: 'Slug (URL)',
  categories_slug_placeholder: 'e.g. vegetables',
  categories_description: 'Description',
  categories_sort_order: 'Sort order',
  categories_list: 'All categories',
  categories_loading: 'Loading…',
  categories_empty: 'No categories yet.',
  categories_active: 'Active',
  categories_update: 'Update',
  categories_create: 'Create',
  categories_confirm_delete: 'Delete this category?',
  categories_yes: 'Yes',
  categories_no: 'No',
  categories_load_error: 'Unable to load categories.',
  categories_save_error: 'Unable to save category.',
  categories_delete_error: 'Unable to delete category.',
  categories_duplicate_slug:
    'A category with this URL slug already exists. Change the slug or use a different name.',
  categories_duplicate_generic:
    'A category with this name or URL slug already exists. Change the values and try again.',
  categories_field_name_taken: 'This name is already used by another category.',
  categories_field_slug_taken: 'This URL slug is already used by another category.',
  categories_unique_hint: 'Category names must be unique. Leave slug blank to generate one from the name.',
  // Hero / landing carousel
  hero_title: 'Landing hero carousel',
  hero_subtitle: 'Manage the full-screen slides on the public landing page.',
  hero_form_edit: 'Edit slide',
  hero_form_new: 'New slide',
  hero_label_image: 'Image',
  hero_label_headline: 'Headline',
  hero_label_subheadline: 'Subheadline',
  hero_label_button_label: 'Button label',
  hero_label_button_link: 'Button link',
  hero_label_sort_order: 'Sort order',
  hero_label_active: 'Active',
  hero_btn_clear: 'Clear',
  hero_btn_save: 'Save changes',
  hero_btn_add_slide: 'Add slide',
  hero_existing_slides: 'Existing slides',
  hero_loading: 'Loading…',
  hero_empty: 'No slides yet. Create your first hero slide on the left.',
  hero_order: 'order',
  hero_button: 'Button',
  hero_link: 'Link',
  hero_btn_edit: 'Edit',
  hero_active: 'Active',
  hero_inactive: 'Inactive',
  hero_btn_delete: 'Delete',
  hero_confirm_delete: 'Delete this hero slide?',
  hero_error_headline: 'Headline is required',
  hero_error_upload: 'Please upload an image',
  hero_shop_now_default: 'Shop now',
  // Catalog / products
  products_title: 'Products & catalog',
  products_subtitle:
    'Manage the products, pricing and images that power your store.',
  products_search_placeholder: 'Search by name or SKU',
  products_search_button: 'Search',
  products_search_clear: 'Clear',
  products_add_button: 'Add product',
  products_hide_form: 'Hide form',
  products_name_label: 'Name',
  products_description_label: 'Description',
  products_image_label: 'Image URL',
  products_unit_label: 'Base unit',
  products_unit_lb: 'Pound (lb)',
  products_unit_kg: 'Kilogram (kg)',
  products_price_label: 'Price per unit',
  products_delivery_fee_label: 'Fixed delivery fee (optional)',
  products_saving: 'Saving…',
  products_update_button: 'Update product',
  products_create_button: 'Create product',
  products_table_title: 'Product catalog',
  products_loading: 'Loading products…',
  products_empty: 'No products yet. Create your first product above.',
  products_table_col_name: 'Name',
  products_table_col_sku: 'SKU',
  products_table_col_category: 'Category',
  products_table_col_price: 'Price / unit',
  products_table_col_sale_price: 'Sale price',
  products_table_col_stock: 'Stock (lb)',
  products_prev_page: 'Prev',
  products_next_page: 'Next',
  products_action_edit: 'Edit',
  // Inventory & purchases
  inventory_title: 'Inventory & stock control',
  inventory_subtitle:
    'Track stock lots, expiry dates and purchase history with fast search and filters.',
  inventory_tab_lots: 'Stock lots',
  inventory_tab_purchases: 'Purchase history',
  inventory_search_button: 'Search',
  inventory_lots_search: 'Search by product name',
  inventory_lots_loading: 'Loading stock lots…',
  inventory_lots_empty: 'No stock lots found.',
  inventory_lots_col_product: 'Product',
  inventory_lots_col_batch: 'Batch',
  inventory_lots_col_purchased: 'Purchased (lb)',
  inventory_lots_col_remaining: 'Remaining (lb)',
  inventory_lots_col_expiry: 'Expiry',
  inventory_prev_page: 'Prev',
  inventory_next_page: 'Next',
  inventory_purchases_search: 'Search by supplier or reference',
  inventory_purchases_from: 'From',
  inventory_purchases_to: 'To',
  inventory_purchases_loading: 'Loading purchases…',
  inventory_purchases_empty:
    'No purchases found for the selected filters.',
  inventory_purchases_col_date: 'Date',
  inventory_purchases_col_supplier: 'Supplier',
  inventory_purchases_col_ref: 'Reference',
  inventory_purchases_col_items: 'Items',
  inventory_purchases_col_total: 'Total',
  inventory_new_purchase_show: 'New purchase',
  inventory_new_purchase_hide: 'Hide form',
  inventory_new_purchase_supplier: 'Supplier',
  inventory_new_purchase_reference: 'Reference',
  inventory_new_purchase_date: 'Purchase date',
  inventory_new_purchase_product: 'Product',
  inventory_new_purchase_product_placeholder: 'Select product',
  inventory_new_purchase_qty: 'Quantity',
  inventory_new_purchase_unit: 'Unit',
  inventory_new_purchase_price: 'Unit price',
  inventory_new_purchase_expiry: 'Expiry date',
  inventory_new_purchase_batch: 'Batch code',
  inventory_new_purchase_submit: 'Save purchase',
  inventory_new_purchase_saving: 'Saving…',
  inventory_new_purchase_error: 'Unable to create purchase.',
  inventory_new_purchase_product_required:
    "Please add at least one item line (click “Add item”).",

  // Budget
  budget_title: 'Budget',
  budget_subtitle: 'Set budget periods. Purchases, losses and other expenses are deducted automatically.',
  budget_periods: 'Budget periods',
  budget_new: 'New period',
  budget_name: 'Period name',
  budget_initial: 'Initial amount',
  budget_create: 'Create',
  budget_loading: 'Loading…',
  budget_empty: 'No budget periods yet.',
  budget_overbudget: 'Over budget',
  budget_hide_detail: 'Hide detail',
  budget_view_detail: 'View detail',
  budget_activate: 'Activate',
  budget_close: 'Close',
  budget_balance: 'Balance',
  budget_adjustment: 'Adjustment (+/-)',
  budget_description: 'Description',
  budget_add_adjustment: 'Add adjustment',
  budget_expense_amount: 'Expense amount',
  budget_expense_desc: 'e.g. Servers, ads',
  budget_add_expense: 'Add expense',
  budget_type: 'Type',
  budget_amount: 'Amount',
  budget_date: 'Date',
  budget_entry_type_initial: 'Initial',
  budget_entry_type_adjustment: 'Adjustment',
  budget_entry_type_purchase: 'Purchase',
  budget_entry_type_loss: 'Loss',
  budget_entry_type_expense_other: 'Other expense',
  budget_created_by: 'Created by',
  budget_employee: 'Employee',
  budget_employee_id: 'Employee ID',
  budget_employee_unknown: 'Unknown employee',
  budget_report_title: 'Budget reports',
  budget_report_subtitle: 'Generate printable PDF reports for any budget period.',
  budget_report_button_download: 'Download PDF',
  budget_report_button_email: 'Send by email',
  budget_report_section_summary: 'Summary',
  budget_report_section_entries: 'All entries',
  budget_report_section_purchases: 'Purchases only',
  budget_report_section_losses: 'Losses only',
  budget_report_section_other: 'Other expenses & adjustments',
  budget_report_generating: 'Generating report…',
  budget_report_success_download: 'Report ready. Your download should start automatically.',
  budget_report_success_email: 'Report will be sent to your email shortly.',
  budget_report_error: 'Unable to generate report.',
  budget_report_moved_hint: 'Detailed reports for this budget period are available in the “Stats & reports” section.',
  reports_title: 'Stats & reports',
  reports_subtitle: 'Generate structured, printable PDF reports across your budget periods.',
  sidebar_order_statistics: 'Order statistics',
  orders_statistics_title: 'Order statistics',
  orders_statistics_subtitle: 'Analyze order distribution, incidents, and country performance.',
  orders_col_order_id: 'Order ID',
  orders_col_total: 'Total',
  orders_preorder_card_payments_heading: 'Card payment records',
  orders_preorder_deposit_payment: 'Deposit (first charge)',
  orders_preorder_balance_payment: 'Balance (after release)',
  orders_preorder_balance_payments_heading: 'Balance charges (after release)',
  orders_preorder_balance_payment_index: 'Charge {{n}}',
  orders_preorder_balance_payment_pending:
    'No balance charge yet. The customer pays the remainder after you release the pre-order.',
  orders_customer_account_deleted_banner:
    'Customer account deleted — this order is archived for operations. Status buttons, delivery batch assign/unassign, incidents, and reship are disabled. You can still use Stripe verification below if checkout must be marked paid.',
  orders_customer_account_deleted_badge: 'Account deleted',
  orders_workflow_frozen_short: 'Archived (no workflow)',
  orders_view_scope: 'Order view',
  orders_filters_label: 'Search & filters',
  orders_stats_error: 'Unable to load order statistics.',
  reports_load_error: 'Unable to load budget periods for reports.',
  reports_period_label: 'Budget period',
  reports_period_loading: 'Loading budget periods…',
  reports_period_empty: 'No budget periods found. Create one first from the Budget section.',
  reports_period_placeholder: 'Select a budget period…',
  reports_period_currency_hint: 'Reports for this period will use currency',
  reports_sections_label: 'Include sections',
  reports_hint_select_period: 'Select a budget period and at least one section to enable report actions.',
  reports_email_panel_title: 'Email delivery (optional)',
  reports_to_email_label: 'To',
  reports_cc_label: 'CC (comma-separated)',
  reports_message_label: 'Custom message (included in email body)',
  reports_message_placeholder: 'Add optional context for the recipient about this budget report…',
  reports_email_hint: 'If no email is provided, the report will be sent to your own admin email address.',

  // Currencies
  currencies_title: 'Currencies',
  currencies_subtitle: 'Add currencies from the known list. They will be available for all users and prices will use live exchange rates.',
  currencies_supported: 'Supported currencies',
  currencies_loading: 'Loading…',
  currencies_none: 'No currencies added yet.',
  currencies_add_from_known: 'Add from known currencies',
  currencies_search_hint: 'Search by code or name and add to supported list.',
  currencies_search_placeholder: 'e.g. GBP, Euro',
  currencies_no_results: 'No matching currencies.',
  currencies_already_added: 'Added',
  currencies_add: 'Add',
  currencies_cannot_remove_usd: 'Cannot remove base currency (USD).',
  currencies_confirm_remove: 'Remove this currency from supported list?',

  // Losses
  losses_title: 'Record losses',
  losses_subtitle: 'Record product losses (expired, damaged, lost). Deducts from stock and from active budget.',
  losses_history: 'Loss history',
  losses_record: 'Record loss',
  losses_product: 'Product',
  losses_search_product: 'Search product',
  losses_lot: 'Lot (optional)',
  losses_quantity: 'Quantity (lb)',
  losses_reason: 'Reason',
  losses_notes: 'Notes',
  losses_submit: 'Record loss',
  losses_col_date: 'Date',
  losses_col_product: 'Product',
  losses_col_lot: 'Lot',
  losses_col_qty: 'Qty (lb)',
  losses_col_value: 'Value',
  losses_col_reason: 'Reason',
  losses_col_notes: 'Notes',
  losses_empty: 'No losses recorded yet.',
  losses_total_value: 'Total loss value',
  losses_filtered: 'filtered',
  losses_filter_by_reason: 'Filter by reason',
  losses_all_reasons: 'All reasons',
  losses_from_date: 'From date',
  losses_to_date: 'To date',
  losses_clear_filters: 'Clear filters',
  loss_reason_expired: 'Expired',
  loss_reason_damaged: 'Damaged',
  loss_reason_lost: 'Lost',
  loss_reason_theft: 'Theft',
  loss_reason_other: 'Other',

  sidebar_payments: 'Payments',
  sidebar_promotions: 'Promotions',
  sidebar_referrals: 'Referrals',
  referrals_stat_pending: 'Pending ref.',
  referrals_stat_qualified: 'Qualified',
  referrals_stat_rejected: 'Rejected',
  referrals_stat_codes: 'Codes issued',
  referrals_stat_signup_sessions: 'Signup sessions',
  referrals_stat_checkout_queue: 'Checkout queue',
  referrals_deactivated_wind_down:
    'Referral program is off for new links and new sign-ups without a saved session. Ongoing: {{pending}} pending referral(s), {{intents}} signup session(s) finishing within 48h, {{checkout}} unpaid checkout(s) with a referral welcome discount.',
  referrals_wind_down_explainer:
    'New public invite links are disabled. Customers who already opened registration with a referral (session saved for 48 hours) can still finish sign-up. Referred customers with a pending first order keep their welcome discount until checkout completes or the order is cancelled.',
  payments_title: 'Payments',
  payments_subtitle:
    'Stripe card payments: customer, order, amount, status, and PCI-safe card metadata (brand, last4). Full card numbers are never stored.',
  payments_load_error: 'Unable to load payments.',
  payments_filter_search: 'Search',
  payments_filter_search_ph: 'Order, intent ID, customer email, receipt email, last4…',
  payments_filter_status: 'Stripe status',
  payments_filter_from: 'From',
  payments_filter_to: 'To',
  payments_apply: 'Apply',
  payments_col_date: 'Date',
  payments_col_customer: 'Customer',
  payments_customer_deleted_badge: 'Account deleted',
  payments_customer_deleted_hint:
    'Payment history is unchanged. The customer profile was removed when the account was deleted; email may show from Stripe receipt or order snapshot.',
  payments_col_order: 'Order',
  payments_col_order_id: 'Order ID',
  payments_col_payment_id: 'Payment ID',
  payments_expand_order_id: 'Order ID',
  payments_expand_payment_id: 'Payment ID',
  payments_col_amount: 'Amount',
  payments_col_amount_tooltip:
    'This row’s payment amount converted to USD (backoffice base). For pre-orders, use Order (USD) for cart total, deposit, and remaining balance.',
  payments_col_order_balance: 'Order (USD)',
  payments_order_total_short: 'Total',
  payments_order_deposit_short: 'Deposit',
  payments_order_remaining_short: 'Remaining',
  payments_col_status: 'Status',
  payments_col_card: 'Card',
  payments_col_type: 'Method type',
  payments_empty: 'No payments found.',
  payments_view: 'Details',
  payments_pci_note:
    'Sensitive card data is tokenized by Stripe. This screen shows only safe display fields your gateway returns after payment.',
  payments_detail_title: 'Payment record',
  payments_detail_error: 'Unable to load payment details.',

  sidebar_group_security: 'Security',
  sidebar_activities: 'Activity log',
  activities_title: 'Activity log',
  activities_subtitle:
    'Audit trail for sign-ins, security events, and internal actions. Filter by time, IP, user IDs, severity, and free text to investigate abuse or operational issues.',
  activities_load_error: 'Unable to load activity log.',
  activities_apply: 'Apply filters',
  activities_clear: 'Clear filters',
  activities_filter_q: 'Search message / category / action',
  activities_filter_q_ph: 'e.g. login, brute',
  activities_filter_category: 'Category (exact)',
  activities_filter_action: 'Action (contains)',
  activities_filter_action_ph: 'e.g. login.success',
  activities_filter_severity: 'Severity',
  activities_filter_from: 'From date',
  activities_filter_to: 'To date',
  activities_filter_actor: 'Actor user ID',
  activities_filter_target: 'Target user ID',
  activities_filter_ip: 'IP (exact)',
  activities_filter_ip_contains: 'IP (contains)',
  activities_filter_subject_type: 'Subject type',
  activities_filter_subject_id: 'Subject ID',
  activities_page_size: 'Rows per page',
  activities_col_time: 'Time',
  activities_col_severity: 'Severity',
  activities_col_category: 'Category',
  activities_col_action: 'Action',
  activities_col_actor: 'Actor',
  activities_col_ip: 'IP',
  activities_col_message: 'Message',
  activities_empty: 'No activity rows match your filters.',
  activities_pagination_info: 'Page {{page}} of {{totalPages}} · {{total}} events',
  activities_detail_ids: 'Identifiers',
  activities_detail_request: 'Request',
  activities_detail_metadata: 'Metadata (JSON)',

  promotions_title: 'Promotions',
  promotions_subtitle:
    'Create discount codes for checkout. Restrict codes to a specific customer account, minimum lifetime spend, or cart subtotal. Stolen codes do not work on the wrong account.',
  promotions_load_error: 'Unable to load promotions.',
  promotions_empty: 'No promotions yet.',
  promotions_col_code: 'Code',
  promotions_col_type: 'Type',
  promotions_col_value: 'Value',
  promotions_col_restricted: 'Account-only',
  promotions_col_expires: 'Expires',
  promotions_col_active: 'Active',
  promotions_col_actions: 'Actions',
  promotions_type_percent: 'Percent',
  promotions_type_fixed: 'Fixed USD',
  promotions_unlimited: '—',
  promotions_deactivate: 'Deactivate',
  promotions_create_open: 'New promotion',
  promotions_form_code: 'Code',
  promotions_form_description: 'Description (internal)',
  promotions_form_discount_type: 'Discount type',
  promotions_form_percent: 'Percent off (0–100)',
  promotions_form_fixed: 'Fixed discount (USD)',
  promotions_form_restricted_customer: 'Restrict to customer email (optional)',
  promotions_form_restricted_ph: 'customer@example.com',
  promotions_form_restricted_hint:
    'If set, only the account with this email can redeem the code—even if someone else sees the code.',
  promo_seg_collapsed_sub:
    'Expand to set filters and search. You can still create codes restricted by customer email without searching.',
  promo_seg_table_idle: 'Expand filters above and click Run search to load customers.',
  promotions_form_min_lifetime: 'Minimum lifetime spend (USD, optional)',
  promotions_form_min_subtotal: 'Minimum order subtotal (USD, optional)',
  promotions_form_max_total: 'Max redemptions total (optional)',
  promotions_form_max_per_customer: 'Max per customer (optional)',
  promotions_form_expires: 'Expires at (optional, local)',
  promotions_form_active: 'Active',
  promotions_save: 'Create promotion',
  promotions_save_error: 'Unable to save promotion.',
  promotions_deactivate_error: 'Unable to deactivate.',

  delivery_batches_title: 'Delivery batches',
  delivery_batches_subtitle:
    'Group paid orders by area and shipping window for route planning. Move orders between batches until they are shipped.',
  delivery_batches_load_error: 'Unable to load delivery batches.',
  delivery_batches_save_error: 'Could not save batch.',
  delivery_batches_create: 'New batch',
  delivery_batches_area_label: 'Area label',
  delivery_batches_area_ph: 'e.g. New York batch',
  delivery_batches_name_optional: 'Internal name (optional)',
  delivery_batches_country_optional: 'Restrict to country (optional)',
  delivery_batches_city_optional: 'Restrict to city (optional)',
  delivery_batches_window_start: 'Window start',
  delivery_batches_window_end: 'Window end',
  delivery_batches_estimated_window: 'Estimated delivery window',
  delivery_batches_notes_optional: 'Notes (optional)',
  delivery_batches_save: 'Create batch',
  delivery_batches_list: 'Batches',
  delivery_batches_empty: 'No batches yet.',
  delivery_batches_select: 'Select a batch to view orders and assignments.',
  delivery_batches_detail_error: 'Could not load this batch.',
  delivery_batches_close: 'Mark closed',
  delivery_batches_close_dispatch: 'Close batch & ship orders',
  delivery_batches_close_confirm:
    'Close this batch? All PAID orders will be shipped in a background job (not on this request). You can leave and come back anytime to see progress.',
  delivery_batches_dispatching:
    'Background job running… orders are being marked SHIPPED. This batch will close automatically when done.',
  delivery_batches_dispatch_progress: '{{processed}} / {{total}} orders processed',
  delivery_batches_dispatch_background:
    'You can leave this page — progress is saved. Open this batch again anytime to see live status.',
  delivery_batches_dispatch_failed: 'Dispatch failed. See details below.',
  delivery_batches_dispatch_partial_title: 'Some orders could not be shipped:',
  delivery_batches_dispatch_notice:
    'Close job queued. Paid orders will be marked SHIPPED in the background, then the batch will close.',
  delivery_batches_delete: 'Delete empty batch',
  delivery_batches_delete_confirm: 'Delete this batch? Only allowed when it has no orders.',
  delivery_batches_orders_count: '{{count}} orders',
  delivery_batches_add_order: 'Assign paid order',
  delivery_batches_pick_order: 'Choose order…',
  delivery_batches_assign: 'Assign',
  delivery_batches_in_batch: 'Orders in this batch',
  delivery_batches_no_orders: 'No orders in this batch yet.',
  delivery_batches_speed: 'Delivery speed',
  delivery_batches_remove: 'Remove from batch',
  sidebar_delivery_batches: 'Delivery batches',
  orders_delivery_speed: 'Delivery',
  orders_batch_label: 'Batch',
  orders_no_batch: 'Not in a batch yet',
  orders_batch_assign_title: 'Delivery batch',
  orders_batch_filter_batches: 'Filter batches',
  orders_batch_filter_placeholder: 'Search batch name or area…',
  orders_batch_pick: 'Choose batch',
  orders_batch_assign_btn: 'Assign',
  orders_batch_unassign: 'Remove from batch',
  orders_receipt_download: 'Receipt (PDF)',
  orders_receipt_error: 'Unable to download receipt.',
  orders_batch_view_batches: 'Open batches',
  orders_progress_title: 'Order progress',
  orders_progress_simple_subtitle: 'Standard fulfillment',
  orders_progress_incident_badge: 'Incident & refund workflow',
  orders_progress_main: 'Fulfillment',
  orders_progress_refund_step: 'Refund',
  orders_progress_branch: 'Delivery issue & reship',
  orders_progress_issue: 'Issue',
  orders_progress_investigating: 'Investigate',
  orders_progress_resolution: 'Resolution',
  orders_progress_reship_prep: 'Reship queue',
  orders_progress_out_again: 'Out (reship)',
  orders_progress_merge_delivered: 'Then merge to Delivered',
  orders_progress_unreachable_short: 'Unreachable',
  orders_progress_damaged_short: 'Damaged',
  orders_progress_fault_employee: 'Staff fault',
  orders_progress_fault_customer: 'Customer fault',
  orders_progress_refund_sidebar:
    'Refund or return in progress — delivery steps may not match a simple happy path.',
  orders_progress_delivered_skipped: 'n/a',
  incident_open_unreachable_confirm_title: 'Confirm unreachable incident',
  incident_open_unreachable_confirm_message:
    'Are you sure you want to mark this order as unreachable?',
  incident_open_unreachable_confirm_message_batched:
    'This order is on batch "{{batch}}". It will be removed from the batch so this unreachable delivery can be handled outside the batch route. Continue?',
  incident_open_damaged_confirm_title: 'Confirm damaged incident',
  incident_open_damaged_confirm_message: 'Are you sure you want to mark this order as damaged?',
  incident_open_damaged_confirm_message_batched:
    'This order is on batch "{{batch}}". It will be removed from the batch so this damaged shipment can be handled outside the batch route. Continue?',
  delivery_batches_create_expand: 'Show create form',
  delivery_batches_create_collapse: 'Hide create form',
  delivery_batches_selected_n: '{{count}} selected',
  delivery_batches_assign_selected: 'Assign selected',
  delivery_batches_suggest_hint: 'Click rows to select or deselect. Orders already in this batch are hidden.',
  delivery_batches_search_hint:
    'Type country and/or town (suggestions come from paid orders), or keywords — matching orders update live.',
  delivery_batches_searching: 'Searching…',
  delivery_batches_assign_country: 'Country',
  delivery_batches_assign_country_ph: 'e.g. United States',
  delivery_batches_assign_town: 'Town / city',
  delivery_batches_assign_town_ph: 'e.g. New York',
  delivery_batches_order_search: 'Keywords (optional)',
  delivery_batches_order_search_ph: 'Order id, customer name, email…',
  delivery_batches_search_required: 'Enter at least one country, town, or keyword, then click Search.',
  delivery_batches_search_button: 'Search',
  delivery_batches_search_empty_prompt:
    'Start typing a country, town, or keyword to search paid orders in real time.',
  delivery_batches_select_all_results: 'Select all in results',
  delivery_batches_select_all_area: 'Select all in this area',
  delivery_batches_clear_selection: 'Clear selection',
  delivery_batches_area_unknown: 'Address incomplete',
  delivery_batches_other_batch: 'In: {{batch}}',
  delivery_batches_no_search_matches: 'No orders match your search.',

  sidebar_delivery_pricing: 'Delivery Settings',
  delivery_pricing_title: 'Delivery pricing',
  delivery_pricing_subtitle:
    'Choose weight-based or driving-time-based fees and configure standard vs express tiers. The store origin for driving time uses the same coordinates as Public contact (saved when you pick your business address from suggestions). Driving times are computed in the background when customers save or update their address.',
  delivery_pricing_load_error: 'Unable to load delivery pricing settings.',
  delivery_pricing_save_error: 'Unable to save settings.',
  delivery_pricing_saved: 'Delivery pricing saved.',
  delivery_pricing_store_section: 'Store origin (for driving time)',
  delivery_pricing_store_public_contact_info:
    'We use the same coordinates as your storefront Public contact address. Pick a suggested address there so latitude and longitude are stored — you do not need to enter them again on this page.',
  delivery_pricing_store_edit_public_contact: 'Edit public contact',
  delivery_pricing_store_resolved: 'Resolved origin',
  delivery_pricing_store_source_public: 'From public contact',
  delivery_pricing_store_source_override: 'Manual override',
  delivery_pricing_store_source_none: 'No coordinates yet',
  delivery_pricing_store_no_coords_hint:
    'Open Public contact, choose country, city, then pick a street address from suggestions so latitude and longitude are saved.',
  delivery_pricing_store_override_section: 'Optional: manual coordinate override',
  delivery_pricing_store_override_hint:
    'Only if you need a different origin than public contact (e.g. dispatch dock). Leave empty to use public contact.',
  delivery_pricing_store_clear_override: 'Use public contact instead',
  delivery_pricing_clear_override_confirm:
    'Remove the manual override and use Public contact coordinates for driving time?',
  delivery_pricing_store_set_override: 'Save override',
  delivery_pricing_latitude: 'Latitude',
  delivery_pricing_longitude: 'Longitude',
  delivery_pricing_label: 'Label (optional)',
  delivery_pricing_mode_label: 'Pricing model',
  delivery_pricing_mode_weight: 'Weight-based (legacy)',
  delivery_pricing_mode_driving: 'Driving time (from store to customer)',
  delivery_pricing_weight_section: 'Weight fees (fallback)',
  delivery_pricing_weight_base: 'Base fee (USD)',
  delivery_pricing_weight_per_lb: 'Per lb (USD)',
  delivery_pricing_express_surcharge: 'Express surcharge (USD)',
  delivery_pricing_weight_days_standard: 'Standard delivery days (weight mode)',
  delivery_pricing_weight_days_express: 'Express delivery days (weight mode)',
  delivery_pricing_driving_section: 'Driving tiers',
  delivery_pricing_standard_days: 'Standard ETA (days)',
  delivery_pricing_express_days: 'Express ETA (days)',
  delivery_pricing_tiers_hint:
    'Each row: max driving minutes (inclusive) and fee in USD. Rows should be sorted by ascending minutes; use a very large max for the last band.',
  delivery_pricing_standard_tiers: 'Standard tiers (JSON array)',
  delivery_pricing_express_tiers: 'Express tiers (JSON array)',
  delivery_pricing_recompute_all: 'Queue recompute for all customers',
  delivery_pricing_recompute_all_confirm:
    'Queue driving-time jobs for every customer? This can take a while.',
  delivery_pricing_recompute_all_done: 'Queued {{count}} jobs.',
  delivery_pricing_recompute_partial:
    'Queued {{queued}} jobs. {{failed}} could not be added to the queue — is Redis running? Check the backend terminal.{{extra}}',
  delivery_pricing_redis_hint:
    'Requires Redis (BullMQ) and GOOGLE_MAPS_API_KEY with Routes API enabled in Google Cloud (not the legacy Distance Matrix).',
  delivery_pricing_readonly:
    'View only. You need DELIVERY_PRICING:UPDATE (or admin) to change these settings.',

  delivery_pricing_rules_section: 'Pricing rules (subtotal + weight + distance + country)',
  delivery_pricing_express_enabled: 'Enable Express delivery',
  delivery_pricing_store_country: 'Store country (ISO code)',
  delivery_pricing_days_standard: 'Standard ETA (days)',
  delivery_pricing_days_express: 'Express ETA (days)',
  delivery_pricing_countries_section: 'Destination countries',
  delivery_pricing_add_country: 'Add country',
  delivery_pricing_country_code: 'Country code',
  delivery_pricing_country_express_enabled: 'Express enabled for this country',
  delivery_pricing_border_fee: 'Border fee (USD)',
  delivery_pricing_base_fee: 'Base fee (USD)',
  delivery_pricing_per_mile: 'Per mile (USD)',
  delivery_pricing_per_lb: 'Per lb (USD)',
  delivery_pricing_us_threshold_section: 'US free / flat threshold',
  delivery_pricing_free_min_subtotal: 'Min subtotal (USD)',
  delivery_pricing_free_max_weight: 'Max weight (lbs)',
  delivery_pricing_free_fee: 'Qualified fee (USD)',
  delivery_pricing_preview_title: 'Preview calculator',
  delivery_pricing_preview_hint: 'Try numbers before saving rules.',
  delivery_pricing_preview_subtotal: 'Subtotal (USD)',
  delivery_pricing_preview_weight: 'Total weight (lbs)',
  delivery_pricing_preview_miles: 'Distance (miles)',
  delivery_pricing_preview_country: 'Destination country',
  delivery_pricing_preview_standard_fee: 'Standard fee',
  delivery_pricing_preview_express_fee: 'Express fee',
  delivery_pricing_preview_express_unavailable: 'Express is unavailable (global or country disabled).',
  delivery_pricing_preview_threshold_applied: 'Threshold applied.',
  delivery_pricing_preview_invalid_country: 'Enter a destination country code (e.g. US).',
  delivery_pricing_preview_country_missing: 'No rule configured for {{country}}.',

  mail_routing_title: 'Email senders',
  mail_routing_subtitle:
    'Choose From and Reply-To addresses per email type. Use verified identities in Amazon SES. The first From address is used for each purpose; add a no-reply Reply-To where needed.',
  mail_routing_load_error: 'Unable to load mail settings.',
  mail_routing_save_error: 'Unable to save mail settings.',
  mail_routing_saved: 'Mail routing saved.',
  mail_routing_hint:
    'Leave a section empty to use the default SES_SOURCE_EMAIL and MAIL_DEFAULT_REPLY_TO from server environment. You can list multiple From lines; only the first is used as the sender (others can be added later as BCC rules).',
  mail_routing_readonly:
    'You can view these settings but only users with “Configure email senders” (MAIL_ROUTING:UPDATE) can change them.',
  mail_routing_from: 'From addresses (one per line)',
  mail_routing_from_help:
    'Must be verified in AWS SES. First address is used as the envelope sender.',
  mail_routing_reply_to: 'Reply-To (no-reply recommended)',
  mail_routing_bcc: 'BCC (optional, internal copies)',
  mail_routing_bcc_help: 'Recipients get a blind copy of the same message (use for staff-only alerts).',
  mail_routing_save: 'Save mail routing',
  mail_routing_saving: 'Saving…',

  mail_routing_purpose_EMAIL_VERIFICATION: 'Email verification',
  mail_routing_purpose_PASSWORD_RESET: 'Password reset',
  mail_routing_purpose_MFA_LOGIN_CODE: 'Staff login code (MFA)',
  mail_routing_purpose_EMPLOYEE_INVITE: 'Employee invite',
  mail_routing_purpose_WELCOME_CUSTOMER: 'Welcome (new customer)',
  mail_routing_purpose_ORDERS_CUSTOMER: 'Order updates (customer)',
  mail_routing_purpose_ORDERS_STAFF: 'Order alerts (staff)',
  mail_routing_purpose_BUDGET_REPORT: 'Budget reports',
  mail_routing_purpose_EXPIRY_ALERT: 'Expiry alerts',
  mail_routing_purpose_GENERAL: 'Other / default',
  mail_routing_purpose_REFERRAL: 'Referral program (customer)',
} as const;

export const enRbac = {
  perm_group: {
    catalog: 'Catalog',
    inventory: 'Inventory',
    orders: 'Orders',
    payments: 'Payments (Stripe)',
    finance: 'Finance',
    settings: 'Settings',
    users: 'Users',
    rbac: 'Access control',
    security: 'Security & audit',
  },
  perm_desc: {
    payment: {
      read:
        'Open the Payments workspace: browse card charges, search and filter, and view full payment detail (Stripe intent, card brand/last4, linked order).',
    },
  },
  perm: {
    product: {
      read: 'Read products',
      create: 'Create products',
      update: 'Update products',
      delete: 'Delete products',
      upload: 'Upload product images',
    },
    category: {
      read: 'Read categories',
      create: 'Create categories',
      update: 'Update categories',
      delete: 'Delete categories',
    },
    supplier: {
      read: 'Read suppliers',
      create: 'Create suppliers',
      update: 'Update suppliers',
      delete: 'Delete suppliers',
    },
    order: {
      read: 'Read orders',
      update: 'Update orders',
    },
    order_detail: {
      read: 'View order details',
    },
    order_incident: {
      read: 'View order incidents',
      update: 'Manage order incidents',
    },
    order_resolution: {
      read: 'View incident resolutions',
      update: 'Resolve order incidents',
    },
    order_reship: {
      create: 'Reship orders after employee fault',
    },
    order_stats: {
      read: 'View order analytics',
    },
    promotion: {
      read: 'View promotions',
      create: 'Create promotions',
      update: 'Update promotions',
      delete: 'Deactivate promotions',
    },
    referral: {
      read: 'View referral program and attributions',
      update: 'Configure referral program and announcement emails',
    },
    payment: {
      read: 'View Stripe payments workspace',
    },
    purchase: {
      read: 'Read purchases',
      create: 'Create purchases',
      update: 'Update purchases',
      delete: 'Delete purchases',
    },
    purchase_order: {
      read: 'Read purchase orders',
      create: 'Create purchase orders',
      update: 'Update purchase orders',
      delete: 'Delete purchase orders',
      complete: 'Complete purchase orders',
      print: 'Print purchase orders',
    },
    stock_lot: {
      read: 'Read stock lots',
      update: 'Update stock lots',
    },
    loss: {
      read: 'Read losses',
      create: 'Record losses',
    },
    budget_period: {
      read: 'Read budget periods',
      create: 'Create budget periods',
      update: 'Update budget periods',
      delete: 'Delete budget periods',
    },
    budget_report: {
      create: 'Generate budget reports',
    },
    currency: {
      read: 'Read currencies',
      update: 'Manage currencies',
    },
    ui_setting: {
      read: 'View display settings',
      update: 'Change display settings',
    },
    mail_routing: {
      read: 'View email sender settings',
      update: 'Configure email senders',
    },
    expiry_alert: {
      read: 'Read expiry alerts',
      update: 'Manage expiry alerts',
    },
    hero: {
      read: 'Read hero slides',
      create: 'Create hero slides',
      update: 'Update hero slides',
      delete: 'Delete hero slides',
      upload: 'Upload hero images',
    },
    employee: {
      read: 'Read employees',
      create: 'Create employees',
      update: 'Update employees',
    },
    customer: {
      read: 'Read customers',
    },
    delivery_pricing: {
      read: 'View delivery pricing settings',
      update: 'Configure delivery pricing and store location',
    },
    role: {
      read: 'Read roles',
      create: 'Create roles',
      update: 'Update roles',
      delete: 'Delete roles',
    },
    activity: {
      read: 'View security activity log (audit trail, IP filters)',
    },
  },
} as const;

export const enCommon = {
  save: 'Save',
  cancel: 'Cancel',
  edit: 'Edit',
  delete: 'Delete',
  saving: 'Saving…',
  empty_placeholder: '—',
  loading: 'Loading…',
  list_loading_title: 'Fetching your data…',
  list_loading_hint:
    'A slow network can make this take a moment. Thanks for your patience.',
  filter: 'Filter',
  clear: 'Clear',
  all: 'All',
  prev: 'Previous',
  next: 'Next',
  remove: 'Remove',
  collapse: 'Collapse',
  expand: 'Expand',
  language: 'Language',
  currency: 'Currency',
} as const;

export const enEmployee = {
  sidebar_profile: 'Profile',
  sidebar_team: 'Team',
  sidebar_security: 'Security',
  title: 'Team profile',
  subtitle: 'Manage your staff profile details used across the back office.',
  sign_in_required: 'Please sign in to view your profile.',
  profile_section_title: 'Staff profile',
  profile_section_subtitle: 'How your details appear in the back office.',
  field_full_name: 'Full name',
  field_role_title: 'Role / title',
  field_phone: 'Phone',
  edit_button: 'Edit',
  save_button: 'Save changes',
  saving_label: 'Saving…',
  cancel_button: 'Cancel',
  readonly_name_label: 'Name',
  readonly_role_label: 'Role / title',
  readonly_phone_label: 'Phone',
  not_set: 'Not set',
  load_error: 'Unable to load profile.',
  save_success: 'Profile updated successfully.',
  save_error: 'Unable to save profile.',
  account_email_label: 'Account email',
  account_role_label: 'Role',
  security_section_title: 'Security',
  current_password_label: 'Current password',
  new_password_label: 'New password',
  confirm_password_label: 'Confirm new password',
  password_mismatch: 'Passwords do not match.',
  password_save_button: 'Update password',
  password_saving_label: 'Updating…',
  password_change_success: 'Password updated successfully.',
  password_change_error: 'Unable to change password.',
  password_open_button: 'Change password',
} as const;

export const enEmployeeInvite = {
  title: 'Complete your invite',
  subtitle:
    'Set a password for your Lewouhmart employee account. Next time you sign in, an MFA code will be sent to your email.',
  invalid_link: 'This invite link is invalid. Please use the link from your email.',
  field_password_label: 'Choose a password',
  field_password_confirm_label: 'Confirm password',
  password_mismatch: 'Passwords do not match.',
  suggest_password: 'Suggest a strong password',
  saving_label: 'Saving…',
  save_button: 'Save password',
  back_to_signin: 'Back to sign in',
  error_generic: 'Unable to complete invite.',
  success_message: 'Your password has been set. You can now sign in.',
} as const;

// NOTE: this file previously had duplicated exports appended; they have been removed.

